import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon";
import { type EmailView, readEmail } from "@/emailView";
import { pdfPages } from "@/pdfThumbnail";

/** What the viewer shows: a photo, every page of a PDF, or a saved email. */
export type ViewerKind = "photo" | "pdf" | "email";

/** Undoing the history step when the viewer closes, put off a moment in case it reopens. */
let stepBack = 0;

/**
 * A receipt full screen, over everything: a photo fitted to the screen, every page of a PDF, or
 * a saved email as it was sent. The X, Escape and the phone's or browser's Back all close it;
 * opening it adds a step to the browser's history so Back has something to undo.
 */
export function ReceiptViewer({
  url,
  fileName,
  kind,
  onClose,
}: {
  url: string;
  fileName: string;
  kind: ViewerKind;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose);
  const [pages, setPages] = useState<string[]>();
  const [email, setEmail] = useState<EmailView>();
  const [failed, setFailed] = useState(false);
  const isPdf = kind === "pdf";

  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    dialog.showModal();

    // Back closes the viewer rather than leaving the page. The step it adds keeps the router's
    // own state, so the router sees the same page when it's undone, plus a mark saying it's ours.
    const ours = () => (window.history.state as { receiptViewer?: boolean } | null)?.receiptViewer;
    // Opened again straight after closing (React does this in development): keep the step.
    window.clearTimeout(stepBack);
    if (!ours())
      window.history.pushState({ ...(window.history.state as object), receiptViewer: true }, "");
    const onPop = () => {
      if (!ours()) close.current();
    };
    window.addEventListener("popstate", onPop);

    // Listened to here rather than with React's onClose, which would also reach a sheet this
    // viewer is opened from.
    const onCancel = (event: Event) => {
      event.preventDefault();
      close.current();
    };
    dialog.addEventListener("cancel", onCancel);

    return () => {
      window.removeEventListener("popstate", onPop);
      dialog.removeEventListener("cancel", onCancel);
      if (dialog.open) dialog.close();
      if (ours()) stepBack = window.setTimeout(() => window.history.back(), 0);
    };
  }, []);

  useEffect(() => {
    if (!isPdf) return;
    const urls: string[] = [];
    let cancelled = false;
    const width = Math.min(1600, Math.round(window.innerWidth * (window.devicePixelRatio || 1)));
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => pdfPages(blob, width))
      .then((blobs) => {
        if (cancelled) return;
        for (const blob of blobs) urls.push(URL.createObjectURL(blob));
        setPages(urls);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [url, isPdf]);

  useEffect(() => {
    if (kind !== "email") return;
    let cancelled = false;
    let read: EmailView | undefined;
    fetch(url)
      .then((response) => response.blob())
      .then(readEmail)
      .then((view) => {
        read = view;
        if (cancelled) view.files.forEach((f) => URL.revokeObjectURL(f.url));
        else setEmail(view);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      read?.files.forEach((f) => URL.revokeObjectURL(f.url));
    };
  }, [url, kind]);

  return (
    <dialog ref={ref} className="receipt-viewer" aria-label={fileName}>
      <div className="receipt-viewer-bar">
        <span className="receipt-viewer-name">{fileName}</span>
        <a className="receipt-viewer-btn" href={url} download={fileName} aria-label="Download">
          <Icon name="download" size={22} />
        </a>
        <button type="button" className="receipt-viewer-btn" onClick={onClose} aria-label="Close">
          <Icon name="x" size={24} />
        </button>
      </div>
      {/* A click on the dark space around the receipt closes it, as the X does. */}
      <div
        className={`receipt-viewer-body ${kind === "email" && email ? "email" : ""}`}
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        {kind === "photo" ? (
          <img className="fit" src={url} alt={`Receipt ${fileName}`} />
        ) : pages ? (
          pages.map((page, index) => (
            <img key={page} src={page} alt={`Page ${index + 1} of ${fileName}`} />
          ))
        ) : email ? (
          <EmailPage email={email} />
        ) : failed ? (
          <p className="receipt-viewer-note">
            This {kind === "email" ? "email" : "PDF"} can&apos;t be shown here.{" "}
            <a href={url} target="_blank" rel="noreferrer">
              Open it in a new tab
            </a>
            .
          </p>
        ) : (
          <p className="receipt-viewer-note">Loading…</p>
        )}
      </div>
    </dialog>
  );
}

/** A saved email: who sent it and when, its attachments, then the email itself. */
function EmailPage({ email }: { email: EmailView }) {
  const sent = email.date ? new Date(email.date) : undefined;
  return (
    <article className="email-view">
      <header className="email-view-head">
        <h2>{email.subject}</h2>
        {email.from && <span>From {email.from}</span>}
        {sent && !Number.isNaN(sent.getTime()) && (
          <span className="muted">
            {sent.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
          </span>
        )}
        {email.files.length > 0 && (
          <span className="email-view-files">
            {email.files.map((file) => (
              <a key={file.url} href={file.url} download={file.name}>
                <Icon name="file" size={14} /> {file.name}
              </a>
            ))}
          </span>
        )}
      </header>
      {/* No scripts, forms or access to the app: only the email, with links opening a new tab. */}
      <iframe
        title={email.subject}
        className="email-view-frame"
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcDoc={email.page}
      />
    </article>
  );
}
